<section class="file-provisioning">
  <header class="fp-header">
    <h3>Source files</h3>
    <span class="fp-gate" [class.allowed]="generationReady()" [class.blocked]="!generationReady()">
      @if (generationReady()) {
        <mat-icon>check_circle</mat-icon> Ready to generate
      } @else if (requiredAbsent().length) {
        <mat-icon>error_outline</mat-icon>
        Missing: {{ missingLabels() }}
        @if (requiredAwaitingUpload().length) {
          · not uploaded: {{ awaitingUploadLabels() }}
        }
      } @else if (requiredAwaitingUpload().length) {
        <!-- Every required zone has a file — it just hasn't been sent yet. Saying "Missing" here
             reads as "you forgot to add them" when the real next step is one click. -->
        <mat-icon>schedule</mat-icon>
        Not uploaded yet: {{ awaitingUploadLabels() }}
      } @else {
        <mat-icon>error_outline</mat-icon>
        Add the required files to generate
      }
    </span>
  </header>

  @if (isLocked) {
    <div class="fp-locked-banner">
      <mat-icon>lock</mat-icon>
      Files are locked — generation has started. Uploads and changes are disabled.
    </div>
  }

  <p class="fp-zones-label">Upload files by category</p>

  <div class="fp-zones">
  @for (category of categories(); track category.category) {
    <div
      class="fp-zone"
      [class.dragover]="dragOver() === category.category"
      [attr.data-status]="category.status"
      (dragover)="onDragOver($event, category.category)"
      (dragleave)="onDragLeave(category.category)"
      (drop)="onDrop($event, category)"
    >
      <div class="fp-zone-head">
        <div class="fp-zone-title">
          <strong>{{ category.label }}</strong>
          <span class="fp-req" [class.required]="category.requirement === 'required'">
            {{ category.requirement }}
          </span>
          <span class="fp-status">{{ statusLabel(category.status) }}</span>
        </div>
        <div class="fp-formats">
          accepts {{ category.acceptedFormats.join(', ') }}
          <span class="fp-multiplicity">· {{ category.multiple ? 'multiple files' : 'single file' }}</span>
        </div>
      </div>

      <label class="fp-droparea">
        <mat-icon>cloud_upload</mat-icon>
        <span>Drag &amp; drop, or <u>browse</u></span>
        <input
          type="file"
          hidden
          [accept]="accept(category)"
          [multiple]="category.multiple"
          (change)="onBrowse($event, category)"
        />
      </label>

      @if (uploadingCount(category.category) > 0) {
        <app-loader [isLoading]="true" />
      }

      <!-- Staged files: added to the zone but not sent until the Upload button is clicked.
           After a failed validate pass each row shows its own outcome (mixed list). -->
      @for (item of stagedFor(category.category); track item) {
        <div class="fp-file fp-file--staged" [attr.data-status]="item.status ?? null">
          <mat-icon class="fp-file-icon">schedule</mat-icon>
          <span class="fp-file-name">{{ item.file.name }}</span>
          <span class="fp-file-size">{{ formatSize(item.file.size) }}</span>
          <span class="fp-file-state">{{ item.status ? statusLabel(item.status) : 'Ready to upload' }}</span>
          <button type="button" class="fp-remove" title="Remove from upload" [disabled]="validating()" (click)="removeStaged(item)">
            <mat-icon>close</mat-icon>
          </button>
          @for (issue of item.issues ?? []; track issue.code) {
            <small class="fp-file-msg">{{ issue.message }}</small>
          }
          <!-- Misplaced file: one click relocates it to the zone that accepts it, rather than
               making the user remove it and drag it in again. -->
          @if (targetZoneFor(item); as target) {
            <button
              type="button"
              class="fp-move"
              [disabled]="validating()"
              (click)="moveStaged(item, target.category)"
            >
              Move to {{ target.label }}
            </button>
          }
        </div>
      }

      <!-- Files confirmed stored by this session's upload (read-only — there is no server
           manifest to re-fetch or manage them through yet). -->
      @for (file of category.files; track file.fileId) {
        <div class="fp-file" data-status="Accepted">
          <mat-icon class="fp-file-icon">description</mat-icon>
          <span class="fp-file-name">{{ file.fileName }}</span>
          @if (file.sizeBytes) {
            <span class="fp-file-size">{{ formatSize(file.sizeBytes) }}</span>
          }
          <span class="fp-file-state">Uploaded</span>
        </div>
      }
    </div>
  }
  </div>

  <!-- Session-level hard failures (TOO_MANY_FILES / AGGREGATE_SIZE_EXCEEDED) — not tied
       to any one file, so they get a banner rather than a row mark. -->
  @if (sessionErrors().length) {
    <div class="fp-session-errors">
      <mat-icon>error_outline</mat-icon>
      <div>
        @for (e of sessionErrors(); track e.code) {
          <div>{{ e.message }}</div>
        }
      </div>
    </div>
  }

  <!-- Upload: sends the staged session to the backend Upload API in one call — it validates
       (same hard checks as /validate) then stores each file independently (story 1.5). -->
  <div class="fp-actions">
    @if (stagedErrors().length) {
      <span class="fp-actions-hint fp-actions-hint--error">
        {{ stagedErrors().length }} file{{ stagedErrors().length === 1 ? '' : 's' }} need attention —
        move {{ stagedErrors().length === 1 ? 'it' : 'them' }} to the right zone or remove
        {{ stagedErrors().length === 1 ? 'it' : 'them' }} to upload
      </span>
    } @else if (staged().length) {
      <span class="fp-actions-hint">
        {{ staged().length }} file{{ staged().length === 1 ? '' : 's' }} ready — not uploaded yet
      </span>
    }
    <button
      type="button"
      class="aecom_btn--solid"
      [disabled]="isLocked || validating() || !staged().length || stagedErrors().length > 0"
      (click)="uploadStaged()"
    >
      {{ validating() ? 'Uploading…' : 'Upload' }}
    </button>
  </div>
</section>
